import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/components/ui/use-toast"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useAuth } from "@/lib/auth.tsx"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { ProfileData } from './Profile'
import { Loader2, Check, ChevronDown, Clock, MapPin, Search } from "lucide-react"
import { useEffect, useState, useMemo } from "react"
import { useTimezone } from "@/hooks/useTimezone"
import { TIMEZONE_REGIONS, getTimezoneInfo, formatTimeInTimezone, detectUserTimezone } from "@/lib/timezone-utils"

const settingsFormSchema = z.object({
  full_name: z.string().min(2, "Name must be at least 2 characters").max(50),
  language: z.string(),
  timezone: z.string(),
  emailNotifications: z.boolean(),
})

// Flatten all timezones for the selector
const ALL_TIMEZONE_OPTIONS = Object.entries(TIMEZONE_REGIONS).flatMap(([regionKey, region]) => {
  if (regionKey === 'COMMON') {
    return region.timezones.map(tz => ({ ...tz, group: region.label, isCommon: true }))
  }
  return region.timezones.map(tz => ({ ...tz, group: region.label, isCommon: false }))
}).reduce((acc, tz) => {
  // Remove duplicates, prioritizing common timezones
  const existing = acc.find(existing => existing.value === tz.value)
  if (!existing) {
    acc.push(tz)
  } else if (tz.isCommon && !existing.isCommon) {
    // Replace with common version
    const index = acc.indexOf(existing)
    acc[index] = tz
  }
  return acc
}, [] as Array<{ value: string; label: string; region: string; group: string; isCommon: boolean }>)

interface TimezoneOption {
  value: string
  label: string
  region: string
  group: string
  isCommon: boolean
  offset?: string
  currentTime?: string
}

interface TimezoneSearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  disabled?: boolean
}

function TimezoneSearchableSelect({ value, onValueChange, disabled }: TimezoneSearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTimezone, setSelectedTimezone] = useState<TimezoneOption | null>(null)
  
  // Enhance timezone options with offset and current time
  const enhancedOptions = useMemo(() => {
    return ALL_TIMEZONE_OPTIONS.map(option => {
      const timezoneInfo = getTimezoneInfo(option.value)
      const currentTime = formatTimeInTimezone(new Date(), option.value)
      
      return {
        ...option,
        offset: timezoneInfo.offsetString,
        currentTime
      }
    })
  }, [])
  
  // Update selected timezone when value changes
  useEffect(() => {
    const selected = enhancedOptions.find(option => option.value === value)
    setSelectedTimezone(selected || null)
  }, [value, enhancedOptions])
  
  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return enhancedOptions
    
    const query = searchQuery.toLowerCase()
    return enhancedOptions.filter(option => 
      option.label.toLowerCase().includes(query) ||
      option.region.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query)
    )
  }, [enhancedOptions, searchQuery])
  
  // Group filtered options
  const groupedOptions = useMemo(() => {
    const groups: Record<string, TimezoneOption[]> = {}
    
    filteredOptions.forEach(option => {
      if (!groups[option.group]) {
        groups[option.group] = []
      }
      groups[option.group].push(option)
    })
    
    return groups
  }, [filteredOptions])
  
  const handleSelect = (option: TimezoneOption) => {
    setSelectedTimezone(option)
    onValueChange(option.value)
    setOpen(false)
    setSearchQuery('')
  }
  
  const handleAutoDetect = () => {
    const detected = detectUserTimezone()
    const detectedOption = enhancedOptions.find(option => option.value === detected)
    if (detectedOption) {
      handleSelect(detectedOption)
    }
  }
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-10"
          disabled={disabled}
        >
          <div className="flex items-center gap-2 flex-1 text-left">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            {selectedTimezone ? (
              <div className="flex flex-col">
                <span className="text-sm">{selectedTimezone.label}</span>
                <span className="text-xs text-muted-foreground">
                  {selectedTimezone.offset} • {selectedTimezone.currentTime}
                </span>
              </div>
            ) : (
              <span>Select timezone...</span>
            )}
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="flex flex-col">
          {/* Search header */}
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder="Search timezones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-8"
            />
          </div>
          
          {/* Auto-detect button */}
          <div className="p-2 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAutoDetect}
              className="w-full justify-start h-8"
            >
              <MapPin className="mr-2 h-4 w-4" />
              Auto-detect my timezone
            </Button>
          </div>
          
          {/* Options list */}
          <div className="max-h-[300px] overflow-auto">
            {Object.entries(groupedOptions).map(([group, options]) => (
              <div key={group}>
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground border-b bg-muted/30">
                  {group}
                </div>
                {options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none"
                  >
                    <div className="flex items-center gap-2 flex-1 text-left">
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{option.offset}</span>
                          <Clock className="h-3 w-3" />
                          <span>{option.currentTime}</span>
                        </div>
                      </div>
                    </div>
                    {selectedTimezone?.value === option.value && (
                      <Check className="h-4 w-4" />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
          
          {filteredOptions.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No timezones found.
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

type SettingsFormValues = z.infer<typeof settingsFormSchema>

interface ProfileSettingsProps {
  profileData: ProfileData | null;
  loading: boolean;
}

export default function ProfileSettings({ profileData, loading }: ProfileSettingsProps) {
  const { updateProfile } = useAuth()
  const { toast } = useToast()
  const { setTimezone, error: timezoneError } = useTimezone()

  const form = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: {
      full_name: '',
      language: 'en',
      timezone: 'UTC',
      emailNotifications: true,
    },
  })

  // Update form when profileData changes
  useEffect(() => {
    if (profileData) {
      const notificationPrefs = profileData.notificationPreferences || {
        emailNotifications: true
      }
      
      // Use detected timezone if no timezone is set in profile
      const userTimezone = profileData.timezone || detectUserTimezone() || 'UTC'
      
      form.reset({
        full_name: profileData.full_name || '',
        language: profileData.preferred_language || 'en',
        timezone: userTimezone,
        emailNotifications: notificationPrefs.emailNotifications,
      })
    }
  }, [profileData, form])

  async function onSubmit(data: SettingsFormValues) {
    try {
      // Update timezone using the hook first
      await setTimezone(data.timezone)
      
      // Then update the rest of the profile
      await updateProfile(data)
      
      toast({
        title: "Settings updated",
        description: "Your profile settings have been updated successfully.",
      })
    } catch (error) {
      console.error('Error updating settings:', error)
      toast({
        title: "Error",
        description: timezoneError || "Failed to update settings. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Language</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select language" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="es">Spanish</SelectItem>
                          <SelectItem value="fr">French</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timezone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Timezone</FormLabel>
                      <FormDescription>
                        Select your timezone for accurate scheduling and notifications
                      </FormDescription>
                      <FormControl>
                        <TimezoneSearchableSelect
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={loading}
                        />
                      </FormControl>
                      {timezoneError && (
                        <p className="text-sm text-destructive mt-1">{timezoneError}</p>
                      )}
                    </FormItem>
                  )}
                />

                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Notification Preferences</h3>
                  
                  <FormField
                    control={form.control}
                    name="emailNotifications"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Email Notifications</FormLabel>
                          <FormDescription>
                            Master switch for all email notifications
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                </div>

                <div className="flex justify-end">
                  <Button type="submit">Save Changes</Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </CardContent>
    </Card>
  )
}
